import { Briefcase, ChevronDown, ArrowDownWideNarrow, ArrowDownAZ, Check } from 'lucide-react';
import { useFilters } from '@/contexts/FilterContext';
import { useUnitStatusOptional } from '@/contexts/UnitStatusContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useFilterPermissions } from '@/hooks/useFilterPermissions';
import type { IndustryType, Unit, UnitFilterData } from '@/types';
import { useState, useMemo, forwardRef } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

interface IndustryFilterProps {
  units?: (Unit | UnitFilterData)[];
  /** When true, shows a green ring highlight for onboarding */
  highlight?: boolean;
}

// Constants defined outside component to avoid recreating on every render
const ALL_INDUSTRY_TYPES: { value: IndustryType; label: string }[] = [
  { value: 'film', label: 'Film' },
  { value: 'event', label: 'Event' },
  { value: 'gov', label: 'Government' },
  { value: 'utility', label: 'Utility' },
  { value: 'relief', label: 'Relief' },
];

// Stable empty array reference to prevent unnecessary re-renders
const EMPTY_UNITS: (Unit | UnitFilterData)[] = [];

export const IndustryFilter = forwardRef<HTMLButtonElement, IndustryFilterProps>(function IndustryFilter({
  units = [],
  highlight = false,
}, ref) {
  const {
    selectedIndustryTypes,
    setSelectedIndustryTypes,
    units: contextUnits,
    isLoading: filterContextLoading
  } = useFilters();
  const _unitStatusContext = useUnitStatusOptional();
  const isMobile = useIsMobile();
  const [sortBy, setSortBy] = useState<'name' | 'count'>('name');
  const [menuOpen, setMenuOpen] = useState(false);

  // "All" is selected if every type is in the selected list
  const isAllSelected = selectedIndustryTypes.length === ALL_INDUSTRY_TYPES.length;

  // Calculate counts from ALL units (no filtering by child filters)
  // Industry is the TOP of the hierarchy - it should always show raw counts
  // regardless of Company/Group/Model/Status selections
  // IMPORTANT: Memoized to prevent infinite re-render loop on mobile
  // (useFilterPermissions has different mobile logic that depends on activeTypes.length)
  const unitsSource = useMemo(() => {
    const unitsArray = units && units.length > 0 ? units : EMPTY_UNITS;
    return unitsArray.length > 0 ? unitsArray : contextUnits;
  }, [units, contextUnits]);
  const typeCounts = useMemo(() => {
    // Don't pre-filter by any other filters - Industry is independent
    return ALL_INDUSTRY_TYPES.reduce((acc, type) => {
      acc[type.value] = unitsSource.filter(u => (u.industry_type || 'film') === type.value).length;
      return acc;
    }, {} as Record<string, number>);
  }, [unitsSource]);

  // Filter out industries with 0 units
  const activeTypes = useMemo(() => {
    return ALL_INDUSTRY_TYPES.filter(type => typeCounts[type.value] > 0);
  }, [typeCounts]);

  // CONSOLIDATED VISIBILITY: Single hook handles RBAC, config, page, and data
  const { showIndustryFilter, loading: permissionsLoading } = useFilterPermissions({
    activeIndustryCount: activeTypes.length
  });

  const sortedTypes = useMemo(() => {
    return [...activeTypes].sort((a, b) => {
      if (sortBy === 'count') {
        const countDiff = typeCounts[b.value] - typeCounts[a.value];
        if (countDiff !== 0) return countDiff;
      }
      return a.label.localeCompare(b.label);
    });
  }, [sortBy, typeCounts, activeTypes]);

  // Calculate how many of the selected industries are actually available
  // IMPORTANT: Must be BEFORE the early return to maintain consistent hook order
  const selectedActiveCount = useMemo(() => {
    return selectedIndustryTypes.filter(type =>
      activeTypes.some(active => active.value === type)
    ).length;
  }, [selectedIndustryTypes, activeTypes]);

  // Single visibility check - all logic is in useFilterPermissions
  if (permissionsLoading || !showIndustryFilter) {
    return null;
  }

  const handleToggle = (type: IndustryType) => {
    if (isAllSelected) {
      setSelectedIndustryTypes([type]);
    } else if (selectedIndustryTypes.includes(type)) {
      const newSelection = selectedIndustryTypes.filter(t => t !== type);
      if (newSelection.length === 0) {
        setSelectedIndustryTypes(ALL_INDUSTRY_TYPES.map(t => t.value));
      } else {
        setSelectedIndustryTypes(newSelection);
      }
    } else {
      const newSelection = [...selectedIndustryTypes, type];
      if (newSelection.length === ALL_INDUSTRY_TYPES.length) {
        setSelectedIndustryTypes(ALL_INDUSTRY_TYPES.map(t => t.value));
      } else {
        setSelectedIndustryTypes(newSelection);
      }
    }
  };

  const handleSelectAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedIndustryTypes(ALL_INDUSTRY_TYPES.map(t => t.value));
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedIndustryTypes([]);
    // Close menu immediately after clearing
    setMenuOpen(false);
  };

  // Trigger button shared between desktop dropdown and mobile sheet
  const triggerButton = (
    <button
      ref={ref}
      className={cn(
        "flex items-center gap-1.5 rounded-md bg-muted/40 hover:bg-accent transition-all duration-200 border border-border/50 hover:border-border text-muted-foreground hover:text-foreground",
        isMobile ? "h-9 px-1.5" : "px-3 xl:px-4 py-2",
        highlight && "ring-2 ring-[#8FB560] ring-offset-2 ring-offset-background"
      )}
    >
      <Briefcase className="h-4 w-4" />
      {!isMobile && <span className="font-medium tracking-wide text-xs hidden xl:inline">INDUSTRY</span>}
      {/* Show badge with selected count */}
      {!filterContextLoading && selectedActiveCount > 0 && (
         <span className="text-[10px] leading-none bg-[#8FB560] text-black rounded-full px-1.5 py-0.5 min-w-[16px] text-center font-semibold">
           {selectedActiveCount}
         </span>
      )}
      <ChevronDown className="h-3.5 w-3.5" />
    </button>
  );

  // Filter list content shared between desktop dropdown and mobile sheet
  const filterContent = (
    <>
      <div className={cn(
        "flex items-center justify-between bg-popover z-10 border-b",
        isMobile ? "px-4 py-3 sticky top-0" : "px-2 py-1.5"
      )}>
        <button
          className="text-[10px] text-muted-foreground uppercase cursor-pointer hover:text-foreground flex items-center gap-1 group font-semibold"
          onClick={() => setSortBy(prev => prev === 'name' ? 'count' : 'name')}
        >
          INDUSTRY
          {sortBy === 'count' ? (
            <ArrowDownWideNarrow className="w-3 h-3 text-primary" />
          ) : (
            <ArrowDownAZ className="w-3 h-3 opacity-0 group-hover:opacity-50" />
          )}
        </button>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleSelectAll}
            className="text-xs text-muted-foreground hover:text-[#8FB560] hover:underline transition-colors"
          >
            All
          </button>
          <span className="text-xs text-[#8FB560]/50">|</span>
          <button
            onClick={handleClear}
            className="text-xs text-muted-foreground hover:text-[#8FB560] hover:underline transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      <div className={cn(isMobile ? "px-4 py-2" : "")}>
        {sortedTypes.map((type) => {
          const typeCount = typeCounts[type.value];
          const isSelected = selectedIndustryTypes.includes(type.value);

          return isMobile ? (
            <button
              key={type.value}
              onClick={() => handleToggle(type.value)}
              className="flex items-center justify-between w-full py-3 border-b border-border/50 last:border-b-0"
            >
              <span className="flex items-center gap-3">
                <span className={cn(
                  "flex h-5 w-5 items-center justify-center rounded border",
                  isSelected ? "bg-[#8FB560] border-[#8FB560]" : "border-muted-foreground/50"
                )}>
                  {isSelected && <Check className="h-3.5 w-3.5 text-black" />}
                </span>
                <span className="text-sm">{type.label}</span>
              </span>
              <span className="text-xs text-muted-foreground">({typeCount})</span>
            </button>
          ) : (
            <DropdownMenuCheckboxItem
              key={type.value}
              checked={isSelected}
              onCheckedChange={() => handleToggle(type.value)}
              onSelect={(e) => e.preventDefault()}
              className="flex items-center justify-between"
            >
              <span>{type.label}</span>
              <span className="text-xs text-muted-foreground ml-2">({typeCount})</span>
            </DropdownMenuCheckboxItem>
          );
        })}
      </div>
    </>
  );

  // Mobile: Full-screen bottom sheet
  if (isMobile) {
    return (
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetTrigger asChild>
          {triggerButton}
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[100dvh] p-0 rounded-t-xl">
          <SheetHeader className="px-4 py-4 border-b border-border">
            <SheetTitle className="text-lg font-semibold">Industry Filter</SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto flex-1">
            {filterContent}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Regular dropdown
  return (
    <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        {triggerButton}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {filterContent}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
